import { Dispatch, ReactNode, SetStateAction, useMemo } from 'react';

import createSafeContext from '#/utils/createSafeContext';
import useLocalStorage from '#/utils/useLocalStorage';
import { getUserPreferencesStorageKey } from '#/features/storage/storage';
import { useWalletView } from '../wallet/wallet-context';

const INITIAL_STATE: State = {
  isToastDisplayEnabled: true,
};

interface State {
  readonly isToastDisplayEnabled: boolean;
}

interface Actions {
  readonly updateToastDisplay: (
    isToastDisplayEnabled: State['isToastDisplayEnabled'],
  ) => void;
}

interface View {
  readonly isToastDisplayEnabled: boolean;
}

export const [useUserPreferencesState, StateCtx] = createSafeContext<State>(
  'UserPreferencesState',
);
export const [useUserPreferencesView, ViewCtx] = createSafeContext<View>(
  'UserPreferencesView',
);
export const [useUserPreferencesActions, ActionsCtx] =
  createSafeContext<Actions>('UserPreferencesActions');

interface ProviderProps {
  readonly children: ReactNode;
}

export function UserPreferencesProvider(props: ProviderProps) {
  const walletView = useWalletView();
  const userId = getUserPreferencesStorageKey(walletView.starknetAddress);
  const [state, setState] = useLocalStorage(userId, INITIAL_STATE);

  const actions = useMemo(() => createActions(setState), [setState]);
  const view = useMemo(() => createView(state), [state]);

  return (
    <StateCtx value={state}>
      <ViewCtx value={view}>
        <ActionsCtx value={actions}>{props.children}</ActionsCtx>
      </ViewCtx>
    </StateCtx>
  );
}

function createView(state: State): View {
  const { isToastDisplayEnabled } = state;

  return {
    isToastDisplayEnabled,
  };
}

function createActions(setState: Dispatch<SetStateAction<State>>): Actions {
  return {
    updateToastDisplay(isToastDisplayEnabled) {
      setState((state) => ({
        ...state,
        isToastDisplayEnabled,
      }));
    },
  };
}
